import React, { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';
import deDE from 'antd/locale/de_DE';
import esES from 'antd/locale/es_ES';
import itIT from 'antd/locale/it_IT';
import nlNL from 'antd/locale/nl_NL';
import ptPT from 'antd/locale/pt_PT';
import ErrorBoundary from './components/ErrorBoundary';
import ServerUnavailable from './components/ServerUnavailable';
import { useServerHealth } from './hooks/useServerHealth';
import './App.css';
import Home from './pages/Home';
import Login from './pages/Login';
import AccountSettings from './pages/AccountSettings';
import CreateUser from './pages/CreateUser';
import HowToGuide from './pages/HowToGuide';
import ImageManager from './pages/ImageManager';
import RuleBuilder from './components/RuleBuilder';
import CustomCostItemsModal from './components/CustomCostItemsModal';
import CostTableManager from './components/CostTableManager';
import EmailConfigManager from './components/EmailConfigManager';
import AuditLog from './pages/AuditLog';
import Dashboard from './pages/Dashboard';
import JourneyFunnel from './pages/JourneyFunnel';
import Leads from './pages/Leads';
import CustomerOnboardingGuide from './pages/CustomerOnboardingGuide';
import OutreachMonitor from './pages/OutreachMonitor';
import SuperAdminOverview from './pages/SuperAdminOverview';
import AdminControlCentre from './pages/AdminControlCentre';
import UserAccessManager from './pages/UserAccessManager';

const VariablesManager = lazy(() => import('./components/VariablesManager'));
const PdfContentManager = lazy(() => import('./components/PdfContentManager'));
const PDFLayoutManager = lazy(() => import('./components/PDFLayoutManager'));
const PdfPageDesigner = lazy(() => import('./pages/PdfPageDesigner'));
const CalculatorPreview = lazy(() => import('./pages/CalculatorPreview'));
const PropertyManager = lazy(() => import('./pages/PropertyManager'));
const WebsiteDemoManager = lazy(() => import('./pages/WebsiteDemoManager'));

const ANTD_LOCALES = {
  en: enUS,
  fr: frFR,
  de: deDE,
  es: esES,
  it: itIT,
  nl: nlNL,
  pt: ptPT,
};

const theme = {
  token: {
    colorPrimary: '#1f7a8c',
    colorSuccess: '#2f9e44',
    colorInfo: '#2563eb',
    colorWarning: '#d97706',
    colorError: '#dc2626',
    borderRadius: 10,
    fontFamily: '"Manrope", "Segoe UI", "Helvetica", "Arial", sans-serif',
    colorBgLayout: '#f7fafc',
  },
  components: {
    Button: {
      controlHeight: 38,
      controlHeightLG: 44,
      fontWeight: 600,
    },
    Card: {
      borderRadiusLG: 14,
    },
    Table: {
      headerBg: '#f8fafc',
      headerColor: '#0f172a',
    },
  },
};

const App = () => {
  const { i18n } = useTranslation();
  const antdLocale = ANTD_LOCALES[i18n.language] || enUS;
  const isServerAvailable = useServerHealth();

  if (!isServerAvailable) {
    return (
      <ConfigProvider theme={theme} locale={antdLocale}>
        <div className='main-container'>
          <ServerUnavailable />
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={theme} locale={antdLocale}>
      <ErrorBoundary>
        <div className='main-container'>
          <BrowserRouter>
            <Suspense fallback={<div role='status' aria-live='polite'>Loading admin tool…</div>}>
              <Routes>
                <Route path='/login' element={<Login />} />
                <Route path='/account-settings' element={<AccountSettings />} />
                <Route path='/create-user' element={<CreateUser />} />
                <Route path='/guide' element={<HowToGuide />} />
                <Route path='/images/:propertyID' element={<ImageManager />} />
                <Route path='/variables/:propertyID' element={<VariablesManager />} />
                <Route path='/rules/:propertyID' element={<RuleBuilder />} />
                <Route path='/custom-costs/:propertyID' element={<CustomCostItemsModal />} />
                <Route path='/pdf-content/:propertyID' element={<PdfContentManager />} />
                <Route path='/cost-table/:propertyID' element={<CostTableManager />} />
                <Route path='/pdf-layout/:propertyID' element={<PDFLayoutManager />} />
                <Route path='/pdf-designer/:propertyID' element={<PdfPageDesigner />} />
                <Route path='/email-config/:propertyID' element={<EmailConfigManager />} />
                <Route path='/property-manager' element={<PropertyManager />} />
                <Route path='/customer-onboarding' element={<CustomerOnboardingGuide />} />
                <Route path='/audit-log' element={<AuditLog />} />
                <Route path='/dashboard' element={<Dashboard />} />
                <Route path='/journey-funnel' element={<JourneyFunnel />} />
                <Route path='/leads' element={<Leads />} />
                <Route path='/super-admin' element={<SuperAdminOverview />} />
                <Route path='/super-admin/control-centre' element={<AdminControlCentre />} />
                <Route path='/super-admin/user-access' element={<UserAccessManager />} />
                <Route path='/super-admin/outreach' element={<OutreachMonitor />} />
                <Route path='/outreach-monitor' element={<Navigate to='/super-admin/outreach' replace />} />
                <Route path='/website-demos' element={<WebsiteDemoManager />} />
                <Route path='/calculator-preview/:propertyID' element={<CalculatorPreview />} />
                <Route path='/' element={<Home />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </div>
      </ErrorBoundary>
    </ConfigProvider>
  );
}

export default App;
